import { useQueries } from "@tanstack/react-query";
import React from "react";

import { buildJobQueryOptions } from "@/lib/jobs/job-utils";
import { useJobsStore } from "@/stores/jobs";
import type {JobHandler, JobState, PollResult} from "@/types/jobs";

export function JobRunner({
  children,
  handlers,
}: {
  children: React.ReactNode;
  handlers: JobHandler[];
}) {
  const jobsById = useJobsStore((jobsState) => jobsState.jobs);
  const updateJob = useJobsStore((jobsState) => jobsState.updateJob);
  const removeJob = useJobsStore((jobsState) => jobsState.removeJob);

  const handlerByType = React.useMemo(() => {
    const handlerMap = new Map<string, JobHandler>();
    for (const handler of handlers) {
      handlerMap.set(handler.type, handler);
    }
    return handlerMap;
  }, [handlers]);

  const activeJobs = React.useMemo(() => {
    return Object.values(jobsById).filter(
      (job) => job.state === "queued" || job.state === "running"
    );
  }, [jobsById]);

  const handledActiveJobs = React.useMemo(() => {
    return activeJobs.filter((job) => handlerByType.has(job.type));
  }, [activeJobs, handlerByType]);

  const queryOptions = React.useMemo(() => {
    return buildJobQueryOptions({
      activeJobs: handledActiveJobs,
      handlers: handlerByType,
    });
  }, [handledActiveJobs, handlerByType]);

  const queryResults = useQueries({ queries: queryOptions });

  const latestQueryResultsRef = React.useRef(queryResults);
  latestQueryResultsRef.current = queryResults;

  const resultsSignatureKey = queryResults
    .map((result: { data: PollResult | undefined; status: any; dataUpdatedAt: any; }) => {
      const data = result.data as PollResult | undefined;
      return `${result.status}:${result.dataUpdatedAt}:${data?.state ?? "none"}`;
    })
    .join("|");

  const lastProcessedStateByJobIdRef = React.useRef<Record<string, JobState | undefined>>({});

  React.useEffect(() => {
    const latestResults = latestQueryResultsRef.current;

    const resultsByJobId = new Map<string, (typeof latestResults)[number]>();
    for (let i = 0; i < handledActiveJobs.length; i++) {
      const job = handledActiveJobs[i];
      const result = latestResults[i];
      if (result) {
        resultsByJobId.set(job.id, result);
      }
    }
    
    for (const job of activeJobs) {
      const handler = handlerByType.get(job.type);
      if (!handler) continue;

      const result = resultsByJobId.get(job.id);
      const data = (result?.data ?? undefined) as PollResult | undefined;
      if (!data) continue;

      updateJob(job.id, { ...(data.patch ?? {}), state: data.state });

      const previousState = lastProcessedStateByJobIdRef.current[job.id];
      if (previousState === data.state) continue;
      lastProcessedStateByJobIdRef.current[job.id] = data.state;

      if (data.state === "success") {
        const latestJobAfterUpdate = useJobsStore.getState().jobs[job.id];
        if (latestJobAfterUpdate && handler.onSuccess) {
          void handler.onSuccess(latestJobAfterUpdate);
        }
        removeJob(job.id);
        continue;
      }

      if (data.state === "failed") {
        const latestJobAfterUpdate = useJobsStore.getState().jobs[job.id];
        if (latestJobAfterUpdate && handler.onFail) {
          void handler.onFail(latestJobAfterUpdate);
        }
        removeJob(job.id);
        continue
      }

      if (data.state === "cancelled") {
        const latestJobAfterUpdate = useJobsStore.getState().jobs[job.id];
        if (latestJobAfterUpdate && handler.onCancel) {
          void handler.onCancel(latestJobAfterUpdate);
        }
        removeJob(job.id);
      }
    }
  }, [activeJobs, handledActiveJobs, handlerByType, resultsSignatureKey, updateJob, removeJob]);

  return <>{children}</>;
}
